import { QueryClient, useMutation } from '@tanstack/react-query';
import { queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import {
  GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY,
  TGetListIntegrationWhatsappResponse,
} from './get-list-integration-whatsapp.api';
import { TChannel } from '../../../_shared/types/channel.type';
// import { TChatPlatformIntegration } from '../../_shared/types/chat-platform.type';

/**
 *
 */
export type TDeleteWhatsappIntegrationResponse = ApiResponse<TChannel>;

export const deleteWhatsappIntegration = async (
  organizationChannelId: string
): Promise<TDeleteWhatsappIntegrationResponse> => {
  const url = `/integration/whatsapp/org-channel/${organizationChannelId}`;

  const config = {
    timeout: 0,
  };

  return axiosInstance.delete(url, config);
};

/**
 * Create Whatsapp React-Query Hook
 */
type UseDeleteWhatsappIntegrationOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteWhatsappIntegration, AxiosErrorCustom>;
};

export const useDeleteWhatsappIntegration = ({ mutationConfig }: UseDeleteWhatsappIntegrationOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteWhatsappIntegration,
    ...restConfig,
  });
};

/**
 * Optimistic Update Delete WhatsApp Integration
 */
export const deleteWhatsappIntegrationOptimistically = (queryClient: QueryClient, organizationChannelId: string) => {
  queryClient.setQueryData<TGetListIntegrationWhatsappResponse>(
    [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY],
    (oldData: TGetListIntegrationWhatsappResponse | undefined) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        payload: {
          ...oldData.payload,
          rows: oldData.payload.rows.filter(
            (integration) => integration.organizationChannelId !== organizationChannelId
          ),
          count: oldData.payload.count - 1,
        },
      };
    }
  );
};

/**
 * Create Whatsapp React-Query Hook
 */
type UseDeleteWhatsappIntegrationOptimisticallyOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteWhatsappIntegration, AxiosErrorCustom>;
};

export const useDeleteWhatsappIntegrationOptimistically = ({
  mutationConfig,
}: UseDeleteWhatsappIntegrationOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation<TDeleteWhatsappIntegrationResponse, AxiosErrorCustom, string>({
    mutationFn: deleteWhatsappIntegration,
    onMutate: async (organizationChannelId: string) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY] });

      const previousData = queryClient.getQueryData([GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY]);

      deleteWhatsappIntegrationOptimistically(queryClient, organizationChannelId);

      return { previousData };
    },
    onError: (_err: unknown, _data: unknown, context: unknown) => {
      if (context && typeof context === 'object' && 'previousData' in context) {
        queryClient.setQueryData(
          [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY],
          (context as { previousData: unknown }).previousData
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY] });
    },
    ...restConfig,
  });
};
